import { request } from "../../utils/request";
import { resolveDisplayMediaUrl } from "../../utils/videoUrl";

export const VIDEO_INFER_STATUS = {
  DRAFT: "0",
  DONE: "1",
  PROCESSING: "2",
  FAILED: "3",
  QUEUED: "4",
} as const;

export const VIDEO_INFER_SCENE_TYPE = {
  TEXT: 1,
  AUDIO: 2,
} as const;

export const VIDEO_INFER_DIRECTION = {
  LANDSCAPE: "1",
  PORTRAIT: "2",
} as const;

const PAGE_SIZE_MIN = 1;
const PAGE_SIZE_MAX = 1000;

const buildVideoInferPageParams = (
  params: VideoInfer.PageQuery
): Record<string, string | number> => {
  const pageNo = Math.max(1, Math.floor(Number(params.pageNo) || 1));
  const rawSize = Math.floor(Number(params.pageSize) || 10);
  const pageSize = Math.min(PAGE_SIZE_MAX, Math.max(PAGE_SIZE_MIN, rawSize));
  const query: Record<string, string | number> = { pageNo, pageSize };

  const name = params.name?.trim();
  if (name) query.name = name;

  const status =
    params.status === undefined || params.status === null
      ? ""
      : String(params.status).trim();
  if (status) query.status = status;

  return query;
};

export const resolveVideoInferMediaUrl = (raw?: string | null): string | null =>
  resolveDisplayMediaUrl(raw);

const isImageUrl = (url: string) =>
  /\.(avif|bmp|gif|jpe?g|png|webp)(\?|#|$)/i.test(url);

export const resolveVideoInferCoverUrl = (item: VideoInfer.Item): string | null =>
  resolveVideoInferMediaUrl(item.coverUrl || item.cover);

export const resolveVideoInferVideoUrl = (item: VideoInfer.Item): string | null => {
  const fromVideo = resolveVideoInferMediaUrl(item.videoUrl || item.path);
  if (fromVideo && !isImageUrl(fromVideo)) return fromVideo;
  return null;
};

/** 分页查询视频创作作品 GET /video/video-infer/page */
export const getVideoInferPageApi = (params: VideoInfer.PageQuery) =>
  request<VideoInfer.PageResponse>("/video/video-infer/page", {
    method: "GET",
    params: buildVideoInferPageParams(params),
  });

/** 查询视频创作作品详情 GET /video/video-infer/get */
export const getVideoInferApi = (id: number) =>
  request<VideoInfer.GetResponse>("/video/video-infer/get", {
    method: "GET",
    params: { id },
  });

export const pickVideoInferScene = (
  item?: VideoInfer.Item | VideoInfer.Detail | null
): VideoInfer.SceneReq & { durationSeconds?: number } | null => {
  if (!item) return null;

  const tasks = Array.isArray(item.tasks) ? item.tasks.filter(Boolean) : [];
  const scenes = Array.isArray(item.scenes) ? item.scenes.filter(Boolean) : [];
  const rows = [...tasks, ...scenes];

  // 按 sceneType 精准挑场景：文本作品常常同时挂着音频产物，
  // 只用「有 textContent / audioUrl」或取第一条会把文本作品认成音频。
  const textRow = rows.find(
    (row) =>
      Number(row.sceneType) === VIDEO_INFER_SCENE_TYPE.TEXT && (row.textContent || "").trim()
  );
  const audioRow = rows.find(
    (row) =>
      Number(row.sceneType) === VIDEO_INFER_SCENE_TYPE.AUDIO && (row.audioUrl || "").trim()
  );
  const source = textRow || audioRow || rows.find((row) => row.textContent || row.audioUrl) || null;

  const explicitType = Number(source?.sceneType ?? item.sceneType);
  const hasExplicitType = Number.isFinite(explicitType) && explicitType > 0;

  const text = (source?.textContent || item.textContent || "").trim();
  // 有显式 sceneType 时不再用顶层 audioUrl 兜底，避免把文本作品拖成音频
  const audio = (source?.audioUrl || (hasExplicitType ? "" : item.audioUrl || "")).trim();

  if (!source && !text && !audio && !hasExplicitType) return null;

  const sceneType = hasExplicitType
    ? explicitType === VIDEO_INFER_SCENE_TYPE.AUDIO
      ? VIDEO_INFER_SCENE_TYPE.AUDIO
      : VIDEO_INFER_SCENE_TYPE.TEXT
    : audio && !text
      ? VIDEO_INFER_SCENE_TYPE.AUDIO
      : VIDEO_INFER_SCENE_TYPE.TEXT;

  return {
    sceneType,
    ...(text ? { textContent: text } : {}),
    ...(audio ? { audioUrl: audio } : {}),
    ...(source?.digitalHumanId || item.digitalHumanId
      ? { digitalHumanId: source?.digitalHumanId || item.digitalHumanId }
      : {}),
    ...(source?.voiceId || item.voiceId ? { voiceId: source?.voiceId || item.voiceId } : {}),
    ...(source?.voiceSourceType || item.voiceSourceType
      ? { voiceSourceType: source?.voiceSourceType || item.voiceSourceType }
      : {}),
    ...(source && "durationSeconds" in source && source.durationSeconds != null
      ? { durationSeconds: Number(source.durationSeconds) }
      : {}),
  };
};

const appendJsonReq = (formData: FormData, req: VideoInfer.SaveReq) => {
  formData.append("req", new Blob([JSON.stringify(req)], { type: "application/json" }));
};

/** 保存或更新音频口播草稿 POST /video/video-infer/save（multipart） */
export const saveVideoInferAudioDraftApi = (req: VideoInfer.SaveReq, file: File) => {
  const formData = new FormData();
  appendJsonReq(formData, req);
  formData.append("file", file);
  return request<VideoInfer.IdResponse>("/video/video-infer/save", {
    method: "POST",
    body: formData,
  });
};

/** 保存或更新口播草稿 POST /video/video-infer/save（JSON，文案场景） */
export const saveVideoInferDraftApi = (req: VideoInfer.SaveReq) =>
  request<VideoInfer.IdResponse>("/video/video-infer/save", {
    method: "POST",
    body: req,
  });

/** 发起视频创作 POST /video/video-infer/create（JSON） */
export const createVideoInferApi = (req: VideoInfer.SaveReq) =>
  request<VideoInfer.IdResponse>("/video/video-infer/create", {
    method: "POST",
    body: req,
  });

/** 发起视频创作 POST /video/video-infer/create（multipart 音频直传） */
export const createVideoInferWithAudioApi = (req: VideoInfer.SaveReq, file: File) => {
  const formData = new FormData();
  appendJsonReq(formData, req);
  formData.append("file", file);
  return request<VideoInfer.IdResponse>("/video/video-infer/create", {
    method: "POST",
    body: formData,
  });
};

/** 预估视频创作消耗积分 POST /video/video-infer/estimate-points */
export const estimateVideoInferPointsApi = (scenes: VideoInfer.EstimateSceneReq[]) =>
  request<VideoInfer.EstimatePointsResponse>("/video/video-infer/estimate-points", {
    method: "POST",
    body: scenes,
  });

/** 生成口播稿 POST /video/video-infer/gen-script */
export const genVideoInferScriptApi = (req: VideoInfer.ScriptGenReq) =>
  request<VideoInfer.ScriptGenResponse>("/video/video-infer/gen-script", {
    method: "POST",
    body: req,
  });

/** 重命名本人视频创作作品 PUT /video/video-infer/rename */
export const renameVideoInferApi = (req: VideoInfer.RenameReq) =>
  request<VideoInfer.BoolResponse>("/video/video-infer/rename", {
    method: "PUT",
    body: {
      id: req.id,
      name: req.name.trim().slice(0, 255),
    },
  });

/** 逻辑删除本人视频创作作品 DELETE /video/video-infer/delete */
export const deleteVideoInferApi = (id: number) =>
  request<VideoInfer.BoolResponse>("/video/video-infer/delete", {
    method: "DELETE",
    params: { id },
  });

/** 重试本人失败作品 POST /video/video-infer/retry */
export const retryVideoInferApi = (id: number) =>
  request<VideoInfer.RetryResponse>("/video/video-infer/retry", {
    method: "POST",
    params: { id },
  });

export const videoInferStatusText = (
  status: VideoInfer.Item["status"]
): string => {
  switch (String(status ?? "")) {
    case VIDEO_INFER_STATUS.DONE:
      return "已完成";
    case VIDEO_INFER_STATUS.PROCESSING:
      return "创作中";
    case VIDEO_INFER_STATUS.FAILED:
      return "失败";
    case VIDEO_INFER_STATUS.QUEUED:
      return "排队中";
    default:
      return "草稿";
  }
};

export const videoInferWorkType = (
  item: VideoInfer.Item
): "talking" | "course" => {
  const raw = String(item.direction ?? "").trim().toLowerCase();
  if (
    raw === "2" ||
    raw === "course" ||
    raw === "lesson" ||
    raw.includes("course") ||
    raw.includes("课件") ||
    raw.includes("视频课")
  ) {
    return "course";
  }
  return "talking";
};

export const videoInferUiStatus = (
  status: VideoInfer.Item["status"]
): "draft" | "done" | "processing" | "failed" | "queued" => {
  switch (String(status ?? "")) {
    case VIDEO_INFER_STATUS.DONE:
      return "done";
    case VIDEO_INFER_STATUS.PROCESSING:
      return "processing";
    case VIDEO_INFER_STATUS.FAILED:
      return "failed";
    case VIDEO_INFER_STATUS.QUEUED:
      return "queued";
    default:
      return "draft";
  }
};
